import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/** Document from GET /scans/:id — the API `data` object (nested qrcode_id, scanned_by, etc.) */
export type CurrentScanPayload = Record<string, unknown> & {
  _id?: string;
  qrcode_id?: Record<string, unknown> & {
    type?: string;
    item_name?: string;
    title?: string;
    created_by?: { _id?: string; first_name?: string; last_name?: string };
  };
  scanned_by?: { _id?: string; guest_token?: string };
};

/** Conversation row from socket `my_rooms_retrieved` */
export interface ChatRoom {
  _id: string;
  name?: string;
  lastMessage?: string;
  updatedAt?: string;
  participants?: { _id: string; first_name?: string; last_name?: string }[];
  unreadCount?: number;
  scan_id?: { _id?: string; qrcode_id?: { type?: string } };
  status?: string;
  last_message?: { content?: string };
}

/** Socket `new_message` shape and/or populated API messages */
export interface ChatMessage {
  _id: string;
  chatRoom?: string;
  senderId?: string;
  sender_id?: { _id?: string };
  text?: string;
  content?: string;
  createdAt: string;
}

interface ChatState {
  chatRooms: ChatRoom[];
  messages: ChatMessage[];
  activeChatRoom: string | null;
  loading: boolean;
  publicToken: string | null;
  receiverId: string | null; // ✅ NEW FIELD
  scannedById: string | null; // ✅ NEW FIELD
  /** Latest public scan payload; used by chat after scan → message */
  currentScan: CurrentScanPayload | null;
}

const initialState: ChatState = {
  chatRooms: [],
  messages: [],
  activeChatRoom: null,
  loading: false,
  publicToken: null,
  receiverId: null,
  scannedById: null, // ✅ INITIALIZED
  currentScan: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChatRooms: (state, action: PayloadAction<ChatRoom[]>) => {
      state.chatRooms = action.payload;
    },

    setActiveChatRoom: (state, action: PayloadAction<string>) => {
      state.activeChatRoom = action.payload;
      state.messages = [];
    },

    setMessagesForChat: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },

    addNewMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },

    prependOldMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = [...action.payload, ...state.messages];
    },

    clearMessages: (state) => {
      state.messages = [];
      state.activeChatRoom = null;
    },

    setPublicToken: (state, action: PayloadAction<string>) => {
      state.publicToken = action.payload;
    },

    // ✅ NEW: SET RECEIVER ID
    setReceiverId: (state, action: PayloadAction<string | null>) => {
      state.receiverId = action.payload;
    },

    setScannedById: (state, action: PayloadAction<string | null>) => {
      state.scannedById = action.payload;
    },

    setCurrentScan: (state, action: PayloadAction<CurrentScanPayload | null>) => {
      state.currentScan = action.payload;
    },

    clearCurrentScan: (state) => {
      state.currentScan = null;
    },
  },
});

export const {
  setChatRooms,
  setActiveChatRoom,
  setMessagesForChat,
  addNewMessage,
  prependOldMessages,
  clearMessages,
  setPublicToken,
  setReceiverId,
  setScannedById,
  setCurrentScan,
  clearCurrentScan,
} = chatSlice.actions;

export default chatSlice.reducer;
